import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    useTheme,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    HowToVote,
    Search,
    ArrowBack,
    NavigateNext,
    LocationOn,
    Lock
} from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../api/axios';

import { VotingBooth } from '../../types/voting_booth';

const VillageDetails = () => {
    const { constituencyId, villageId } = useParams<{ constituencyId: string; villageId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();

    // State
    const [search, setSearch] = useState('');

    // Fetch Village Details (for header)
    const { data: village, isLoading: isLoadingVillage } = useQuery({
        queryKey: ['village', villageId],
        queryFn: async () => {
            const res = await api.get(`/tenant/villages/${villageId}`);
            return res.data?.data || res.data;
        },
        enabled: !!villageId
    });

    // Fetch Voting Booths for this Village
    const { data: boothsData, isLoading: isLoadingBooths } = useQuery({
        queryKey: ['voting-booths', villageId, search],
        queryFn: async () => {
            const params: any = { village_id: villageId, per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/tenant/voting-booths', { params });
            return res.data;
        },
        enabled: !!villageId,
        placeholderData: keepPreviousData,
    });

    const booths = boothsData?.data?.data || boothsData?.data || [];

    const isLoading = isLoadingVillage || isLoadingBooths;
    const totalBooths = booths.length;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/admin/citizens" sx={{ display: 'flex', alignItems: 'center' }}>
                        Constituencies
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/admin/citizens/${constituencyId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {village?.constituency?.name || 'Constituency'}
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {village?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(`/admin/citizens/${constituencyId}`)}
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        {village?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Viewing voting booths in {village?.name}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 7 }}>
                        <Chip
                            label={`${totalBooths} Booths`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                        <Chip
                            icon={<Lock sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                            label="Read-only"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                    </Stack>
                </Box>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Voting Booths List
                </Typography>
                <TextField
                    placeholder="Search booths..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    // Loading Skeletons
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 180, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="60%" />
                                            <Skeleton variant="text" width="40%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (booths as VotingBooth[]).length > 0 ? (
                    (booths as VotingBooth[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    },
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/admin/citizens/${constituencyId}/villages/${villageId}/booths/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(124, 58, 237, 0.1)',
                                                color: '#7c3aed',
                                                width: 44,
                                                height: 44,
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem'
                                            }}
                                        >
                                            {item.booth_id.substring(0, 2)}
                                        </Avatar>
                                        <Chip
                                            label={`#${item.booth_id}`}
                                            size="small"
                                            sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}
                                        />
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
                                        {item.name}
                                    </Typography>

                                    <Paper variant="outlined" sx={{ p: 1, mt: 2, bgcolor: theme.palette.background.default, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOn fontSize="small" color="action" />
                                        <Typography variant="caption" color="text.secondary">
                                            {village?.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Citizens: {item.citizens_count}
                                        </Typography>
                                    </Paper>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <HowToVote sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No booths found matching "${search}"` : "No voting booths in this village yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Voting booths are managed by the Super Admin
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default VillageDetails;
